"use client"

import Link from "next/link"
import cn from "classnames"

import { useFooter } from "~/components/foundations/FooterProvider"
import { HubspotForm } from "~/components/primitives/HubspotForm"
import type { SupportedIcons } from "~/components/primitives/icon"
import { Icon } from "~/components/primitives/icon"

import s from "./Footer.module.scss"

export const typename = "typeBuilderModuleFooter_Entry"

const ICON_MAP: Record<string, SupportedIcons> = {
  facebook: "facebook",
  instagram: "instagram",
  x: "x",
  youtube: "youtube",
  linkedin: "linkedin",
  pressRelease: "press-release",
}

export const Footer = () => {
  const { mainNav, legalLinks, copyright, socialLinks, headline, subheadline } =
    useFooter()

  return (
    <footer className={cn(s.footer, "z-footer")} data-element="footer">
      <div className={cn(s.inner, "container", "container-fluid")}>
        <div className={s.left}>
          <div className={s.logoLink}>
            <Link href="/" aria-label="Meraki - Work with Soul Home">
              <Icon id="logo-footer" />
            </Link>
          </div>

          <div className={s.navSocialGrid}>
            <nav aria-label="Footer Navigation" className={s.footerNav}>
              <ul className={s.navLinks}>
                {mainNav.map((item, index) => (
                  <li key={`footer_nav_${index}`} className={s.navItem}>
                    <Link
                      href={item.url}
                      target={item.target ?? undefined}
                      rel={
                        item.target === "_blank"
                          ? "noopener noreferrer"
                          : undefined
                      }
                      className={s.navLink}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <ul className={s.socialLinks} aria-label="Social media links">
              {socialLinks.map((link, index) => {
                const iconId = link.platform
                  ? ICON_MAP[link.platform]
                  : undefined
                return (
                  <li key={`footer_social_${index}`} className={s.socialItem}>
                    <Link
                      href={link.url}
                      target={link.target ?? "_blank"}
                      rel="noopener noreferrer"
                      aria-label={`Follow Meraki on ${link.label}`}
                      className={s.socialLink}
                    >
                      <span className={s.socialLinkText}>{link.label}</span>
                      {iconId && (
                        <Icon
                          id={iconId}
                          width={20}
                          height={20}
                          aria-hidden="true"
                        />
                      )}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>

        <div className={s.right}>
          {(headline || subheadline) && (
            <section
              aria-labelledby="footer-headline"
              className={s.marketingCopy}
            >
              {headline && (
                <h2 id="footer-headline" className={s.headline}>
                  {headline}
                </h2>
              )}
              {subheadline && <p className={s.subheadline}>{subheadline}</p>}
            </section>
          )}
          <div className={s.formContainer}>
            <HubspotForm formId="b2bb2354-8e5b-4b28-b85a-d3f3222a995c" />
          </div>
        </div>
      </div>

      <div className={cn(s.bottomBar, "container", "container-fluid")}>
        {copyright && (
          <p className={s.copyright}>
            &copy; {new Date().getFullYear()} {copyright}
          </p>
        )}
        {legalLinks.length > 0 && (
          <ul className={s.legalLinks} aria-label="Legal links">
            {legalLinks.map((item, index) => (
              <li key={`footer_legal_${index}`}>
                <Link
                  href={item.url}
                  target={item.target ?? undefined}
                  rel={
                    item.target === "_blank" ? "noopener noreferrer" : undefined
                  }
                  className={s.legalLink}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </footer>
  )
}
